import type {CustomerV2} from '@api/capillary-v2/types';
import {z} from 'zod';
import {PasswordSchema} from './password';

export const SMS_PHONE_REQUIRED_MESSAGE = 'Mobile number needed to receive text messages';

export function isPhoneValidForSmsOptIn(data: {
  phoneNumber?: string;
  smsTransactional?: boolean;
  smsPromotional?: boolean;
}): boolean {
  if (!data.smsTransactional && !data.smsPromotional) {
    return true;
  }

  return (data.phoneNumber ?? '').replace(/\D/g, '').length === 10;
}

export const RegistrationSchema = z
  .object({
    firstName: z.string().min(1, {message: 'First name is required'}),
    lastName: z.string().min(1, {message: 'Last name is required'}),
    email: z.string().email('Invalid email address'),
    phoneNumber: z
      .string()
      .refine(
        (phone) => {
          const digitsOnly = phone.replace(/\D/g, '');
          return digitsOnly.length === 0 || digitsOnly.length === 10;
        },
        {message: 'Invalid phone number'}
      )
      .optional(),
    birthMonth: z.string().optional(),
    birthDay: z.string().optional(),
    birthYear: z.string().optional(),
    password: PasswordSchema,
    emailTransactional: z.boolean().optional(),
    emailPromotional: z.boolean().optional(),
    smsTransactional: z.boolean().optional(),
    smsPromotional: z.boolean().optional(),
    optIn: z.boolean().optional(),
    sms: z.boolean().optional(),
    inviteCode: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.birthMonth && data.birthDay && data.birthYear) {
        const birthDate = new Date(
          Number(data.birthYear),
          Number(data.birthMonth) - 1,
          Number(data.birthDay)
        );
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        if (
          age < 16 ||
          (age === 16 && monthDiff < 0) ||
          (age === 16 && monthDiff === 0 && dayDiff < 0)
        ) {
          return false;
        } else {
          return true;
        }
      }

      return false;
    },
    {
      message: 'You must be at least 16 years old.',
      path: ['birthYear'],
    }
  )
  .refine(isPhoneValidForSmsOptIn, {
    message: SMS_PHONE_REQUIRED_MESSAGE,
    path: ['phoneNumber'],
  });

export type Registration = z.infer<typeof RegistrationSchema>;

export function toCustomerFields(registration: Registration): Partial<CustomerV2> {
  const {email, firstName, lastName, phoneNumber, birthMonth, birthDay, birthYear} = registration;

  return {
    profiles: [
      {
        firstName,
        lastName,
        identifiers: [
          {
            type: 'email',
            value: email,
          },
          ...(phoneNumber && phoneNumber.length > 0
            ? [
                {
                  type: 'mobile',
                  value: phoneNumber,
                },
              ]
            : []),
        ],
        source: 'INSTORE',
        fields: {
          home_phone: phoneNumber ?? '',
        },
      },
    ],
    extendedFields: {
      data_sharing_consent: '',
      dob: `${birthYear}-${birthMonth}-${birthDay}T18:00:00-06:00`,
    },
  };
}
